import { useSyncExternalStore } from 'react'

/**
 * IP 制限による拒否（ADR-0088 決定 7）の検知と、拒否画面へ渡す状態。
 *
 * api は許可リスト外の接続元を `403` かつ `errors[0].code = 'ip_not_allowed'`（拡張メンバー `clientIp`）で拒否する。
 * SAML ログインだけは callback からフロントへのリダイレクトで、`?error=ip_not_allowed&clientIp=<IP>` が付いて戻る。
 * どちらも同じ状態へ集めて、アプリ全体を拒否画面に切り替える。
 *
 * 分岐は `errors[].code` で行い、`detail` の文言では行わない（reCAPTCHA の 403 `recaptcha_failed` と区別するため）。
 */

export const IP_NOT_ALLOWED_CODE = 'ip_not_allowed'

/** SAML のリダイレクトで受け取る `clientIp` の形式。これに合わない値は表示しない。 */
const IPV4_PATTERN = /^\d{1,3}(\.\d{1,3}){3}$/

export interface IpNotAllowedState {
  /** 表示できる接続元 IP。取れない・形式が不正な場合は null（画面は IP なしで出す）。 */
  clientIp: string | null
}

type Listener = () => void

let state: IpNotAllowedState | null = null
const listeners = new Set<Listener>()

const emit = (): void => {
  listeners.forEach((listener) => { listener() })
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return value !== null && typeof value === 'object'
}

/** 拒否画面を出す状態にする。 */
export const reportIpNotAllowed = (clientIp: string | null): void => {
  state = { clientIp }
  emit()
}

/**
 * 403 の応答本文が IP 制限の拒否なら、その状態にする。拒否だったかを返す。
 * `errors[0].code` だけで判定する。
 */
export const reportIpNotAllowedFromProblem = (body: unknown): boolean => {
  if (!isRecord(body) || !Array.isArray(body.errors)) return false
  const first: unknown = body.errors[0]
  if (!isRecord(first) || first.code !== IP_NOT_ALLOWED_CODE) return false
  const clientIp = typeof body.clientIp === 'string' && body.clientIp.trim() !== '' ? body.clientIp : null
  reportIpNotAllowed(clientIp)
  return true
}

/**
 * fetch の応答が 403 `ip_not_allowed` なら拒否画面の状態にする。本文は呼び出し側も読めるよう clone して読む。
 * 403 以外・JSON でない本文では何もしない。
 */
export const reportIpNotAllowedFromResponse = async (res: Response): Promise<void> => {
  if (res.status !== 403 || typeof res.clone !== 'function') return
  try {
    reportIpNotAllowedFromProblem(await res.clone().json())
  } catch {
    // 本文が JSON でない 403 は IP 制限の拒否ではない。何もしない。
  }
}

/**
 * SAML の拒否のリダイレクトで付くクエリ（`error=ip_not_allowed&clientIp=<IP>`）を読み、拒否画面の状態にする。
 * 読んだ後、再読み込みで拒否画面が残らないよう、この 2 つのクエリをアドレスバーから取り除く。
 * `clientIp` は IPv4 の形式のときだけ使う。
 */
export const consumeIpNotAllowedFromLocation = (): boolean => {
  const url = new URL(window.location.href)
  if (url.searchParams.get('error') !== IP_NOT_ALLOWED_CODE) return false

  const clientIp = url.searchParams.get('clientIp')
  reportIpNotAllowed(clientIp != null && IPV4_PATTERN.test(clientIp) ? clientIp : null)

  url.searchParams.delete('error')
  url.searchParams.delete('clientIp')
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
  return true
}

/** テスト用。状態を空に戻す。 */
export const resetIpNotAllowed = (): void => {
  state = null
  emit()
}

const subscribe = (listener: Listener): (() => void) => {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

const getSnapshot = (): IpNotAllowedState | null => state

/** 拒否画面を出すべき間は状態を返す。 */
export const useIpNotAllowed = (): IpNotAllowedState | null => {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
